import React, { createContext, useContext, ReactNode } from 'react';
import { useAuth } from './AuthContext';

interface ThemeContextType {
  isLightTheme: boolean;
  themeClasses: {
    background: string;
    card: string;
    text: string;
    textSecondary: string;
    border: string;
    input: string;
    button: string;
    buttonSecondary: string;
    navigation: string;
    navigationBorder: string;
  };
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { user } = useAuth();
  
  // Light theme for group_a users, dark theme for founders and owners
  const isLightTheme = user?.accountType === 'group_a';

  const themeClasses = {
    background: isLightTheme 
      ? 'bg-white' 
      : 'bg-gray-900',
    card: isLightTheme 
      ? 'bg-white rounded-2xl shadow-[0_8px_30px_rgb(201,227,225,0.9)] hover:shadow-[0_8px_40px_rgb(201,227,225,0.8)] transition-all duration-300 p-6 border-0' 
      : 'bg-gray-800 border border-gray-700 p-6',
    text: isLightTheme ? 'text-gray-900' : 'text-white',
    textSecondary: isLightTheme ? 'text-gray-600' : 'text-gray-400',
    border: isLightTheme ? 'border-gray-100' : 'border-gray-700',
    input: isLightTheme 
      ? 'bg-white border border-gray-200 text-gray-900 placeholder-gray-500 focus:ring-cyan-500 focus:border-cyan-500 rounded-xl shadow-sm' 
      : 'bg-gray-800 border border-gray-700 text-white placeholder-gray-400 focus:ring-primary-500 focus:border-transparent',
    button: isLightTheme 
      ? 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 rounded-xl' 
      : 'bg-primary-600 hover:bg-primary-700 text-white',
    buttonSecondary: isLightTheme 
      ? 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-300' 
      : 'bg-gray-600 hover:bg-gray-700 text-white',
    navigation: isLightTheme 
      ? 'bg-white/80 backdrop-blur-md border-t border-gray-100 shadow-lg' 
      : 'bg-gray-800 border-t border-gray-700',
    navigationBorder: isLightTheme ? 'border-gray-100' : 'border-gray-700',
  };

  const value: ThemeContextType = {
    isLightTheme,
    themeClasses,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}; 